import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { StorefrontIcon, PaintBrushBroadIcon, UploadSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DEFAULT_COLOR = "#007AFF";
const PRESETS = ["#007AFF", "#39FF14", "#FF3B30", "#FFEA00", "#A855F7", "#F97316", "#22D3EE", "#EC4899"];

/**
 * Downscale an uploaded image to <=512px on the long edge and return a JPEG data URL.
 */
const compressImage = (file, maxDim = 512, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const SettingsPage = () => {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [officeName, setOfficeName] = useState("");
  const [officeLogo, setOfficeLogo] = useState(null);
  const [themeColor, setThemeColor] = useState(DEFAULT_COLOR);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setOfficeName(user?.office_name || "");
    setOfficeLogo(user?.office_logo || null);
    setThemeColor(user?.theme_color || DEFAULT_COLOR);
  }, [user]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) {
      toast.error(t("logoTooLarge"));
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      if (dataUrl.length > 700_000) {
        toast.error(t("logoTooLarge"));
        return;
      }
      setOfficeLogo(dataUrl);
    } catch {
      toast.error("Error reading image");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", {
        office_name: officeName,
        office_logo: officeLogo,
        theme_color: themeColor,
      });
      setUser(data);
      toast.success(t("savedSettings"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/settings</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t("settingsPage")}</h1>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Office branding */}
        <section className="border border-white/10 bg-[#121212]">
          <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
            <div className="p-2 border border-white/10 bg-[#0A0A0A]">
              <StorefrontIcon size={22} weight="bold" style={{ color: themeColor }} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">{t("officeSection")}</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{t("officeNameHint")}</p>
            </div>
          </div>

          <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-start">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("officeLogo")}</Label>
              <div
                data-testid="logo-preview"
                className="w-[160px] h-[160px] border border-white/10 bg-[#0A0A0A] flex items-center justify-center overflow-hidden"
              >
                {officeLogo ? (
                  <img src={officeLogo} alt="logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-zinc-600 uppercase tracking-[0.2em] text-center px-3">
                    {t("uploadLogo")}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFile}
                  data-testid="logo-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="logo-upload-btn"
                  className="inline-flex items-center gap-1.5 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-3 py-1.5 text-xs transition-colors"
                >
                  <UploadSimpleIcon size={12} weight="bold" /> {t("uploadLogo")}
                </button>
                {officeLogo && (
                  <button
                    type="button"
                    onClick={() => setOfficeLogo(null)}
                    data-testid="logo-remove-btn"
                    className="inline-flex items-center gap-1.5 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-3 py-1.5 text-xs text-[#FF3B30] transition-colors"
                  >
                    <TrashIcon size={12} weight="bold" /> {t("removeLogo")}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-zinc-500">{t("logoHint")}</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("officeName")}</Label>
              <Input
                data-testid="office-name-input"
                value={officeName}
                onChange={(e) => setOfficeName(e.target.value)}
                placeholder="مكتب الأمير للتأجير"
                className="bg-[#0A0A0A] border-white/10 text-lg"
              />
            </div>
          </div>
        </section>

        {/* Theme color */}
        <section className="border border-white/10 bg-[#121212]">
          <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
            <div className="p-2 border border-white/10 bg-[#0A0A0A]">
              <PaintBrushBroadIcon size={22} weight="bold" style={{ color: themeColor }} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">{t("themeSection")}</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{t("themeDesc")}</p>
            </div>
          </div>
          <div className="p-4 sm:p-6 space-y-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("themeColor")}</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeColor}
                    onChange={(e) => setThemeColor(e.target.value)}
                    data-testid="theme-color-picker"
                    className="w-14 h-14 bg-transparent border border-white/10 cursor-pointer"
                  />
                  <div className="font-mono text-lg" style={{ color: themeColor }} data-testid="theme-color-value">
                    {themeColor.toUpperCase()}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setThemeColor(DEFAULT_COLOR)}
                data-testid="theme-reset-btn"
                className="border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors"
              >
                {t("resetTheme")}
              </button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">{t("presetColors")}</Label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setThemeColor(c)}
                    data-testid={`preset-${c.replace("#", "")}`}
                    aria-label={c}
                    className={`w-10 h-10 border-2 transition-transform hover:scale-110 ${
                      themeColor.toLowerCase() === c.toLowerCase() ? "border-white" : "border-white/10"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {/* Live preview */}
            <div className="border border-white/10 bg-[#0A0A0A] p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3">Preview</div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  style={{ backgroundColor: themeColor, color: "#0A0A0A" }}
                  className="px-4 py-2 text-sm font-semibold"
                  data-testid="preview-btn"
                >
                  {t("save")}
                </button>
                <span className="text-sm" style={{ color: themeColor }}>{t("appName")}</span>
                <span
                  className="tl-dot"
                  style={{ backgroundColor: themeColor, boxShadow: `0 0 12px ${themeColor}` }}
                />
              </div>
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save-btn"
            style={{ backgroundColor: themeColor, color: "#0A0A0A" }}
            className="px-6 py-3 text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity"
          >
            {t("save")}
          </button>
        </div>
      </form>
    </div>
  );
};
